import React, { useEffect, useState, useContext } from "react";
import TimeSince from "components/TimeSince/TimeSince";
import { __callbacks } from "Utils/Callbacks/Callback";
import { __chatQueue } from "Utils/ChatQueue/ChatQueue";
import { ConfigContext } from "Utils/Contexts";
import { AvatarComponent } from 'avatar-initials';
import { __utils } from 'Utils/Utils';
import { __chats } from 'Utils/Chats/Chats';
import styles from "./Conversation.module.scss";
import { IChatMessageFragment } from "Utils/Interfaces";

interface ConversationProps {
    chatMessageFragment: IChatMessageFragment;
    backState: any;
}

const Conversation: React.FC<ConversationProps> = (props) => {
    const [time, setTime] = useState(props.chatMessageFragment?.time || 0);
    const [operator, setOperator] = useState(props.chatMessageFragment?.props?.userName || "Unknown");
    const [message, setMessage] = useState(props.chatMessageFragment?.props?.text || "");
    const configContext = useContext(ConfigContext);
    const [thisConfig, setThisConfig] = useState<any>(null);
    const [ureadMsgs, setUreadMsgs] = useState<number>(0);

    useEffect(() => {
        setThisConfig(configContext.containersMetaData?.containers?.startConversation || null);
    }, [configContext]);

    useEffect(() => {
        if (props.chatMessageFragment) {
            setTime(props.chatMessageFragment.time);
            setOperator(props.chatMessageFragment.props?.userName || "Unknown");
            setMessage(props.chatMessageFragment.props?.text || "");
        }
    }, [props]);

    useEffect(() => {
        // Listen to chat message event to know unread messages 
        const handleChatMessage = () => {
            const chatQueue = __chatQueue.totalMsgs(props.chatMessageFragment?.chatId);
            setUreadMsgs(chatQueue);
        };

        const callbackId = __callbacks.onChatMessage(handleChatMessage);

        // Clean up the listener
        return () => {
            __callbacks.chatMessage.remove(callbackId); // Use the callback ID to remove the listener
        };
    }, [props.chatMessageFragment?.chatId]);

    const onClick = () => {
        if (props.chatMessageFragment) {
            __callbacks.continueConversation.trigger({
                conversationId: props.chatMessageFragment.chatId,
                backState: props.backState
            });

            sessionStorage.setItem('last_Chat' + window.location.search.substr(1).split("&")[0].split("=")[1], props.chatMessageFragment.chatId);

            // Force the state to update immediately after click
            setTimeout(() => {
                __callbacks.conversation.trigger({ chatState: { conversationId: props.chatMessageFragment.chatId } });
            }, 0);
        }
    };

    const stripSummary = (message: any) => {
        let text = "";
        if (message?.props?.text) {
            text = message.props.text;
        } else if (message?.messageNo > 1) {
            text = checkFragSummary(__chats.getLastFragment(message.chatId, message.messageNo - 1));
        }

        // Strip HTML tags
        text = __utils.HTMLstrip(text);

        // Trim to 30 characters
        if (text.length > 30) {
            text = text.substring(0, 30) + "...";
        }

        return text;
    };

    const checkFragSummary = (fragment: IChatMessageFragment | undefined): string => {
        if (fragment !== undefined) {
            if (fragment.messageType === "MessageReceive" && fragment.props?.text.length > 0) {
                const text = fragment.props.text;
                return text;
            } else {
                if (fragment.messageNo > 1) {
                    return checkFragSummary(__chats.getLastFragment(fragment.chatId, fragment.messageNo - 1));
                } else {
                    return "";
                }
            }
        } else {
            return "";
        }
    };

    return (
        <div className={styles.container} onClick={onClick}>
            <div className={styles["container-avatars"]}>
                <div className={`${styles["container-avatars"]} config--startConversation-avatars`}>
                    {thisConfig?.avatars?.metaData[0]?.fullName &&
                        <div className={`${styles.avatar} ${styles["avatar-1"]} avatar`}>
                            <AvatarComponent
                                classes="rounded-full"
                                useGravatar={false}
                                size={44}
                                color="#FFFFFF"
                                background="#A13808"
                                fontSize={16}
                                fontWeight={400}
                                offsetY={24}
                                initials={`${thisConfig?.avatars?.metaData[0]?.fullName[0]}`}
                            />
                        </div>
                    }
                    {thisConfig?.avatars?.metaData[1]?.fullName &&
                        <div className={`${styles.avatar} ${styles["avatar-2"]} avatar`}>
                            <AvatarComponent
                                classes="rounded-full"
                                useGravatar={false}
                                size={44}
                                color="#FFFFFF"
                                background="#A13808"
                                fontSize={16}
                                fontWeight={400}
                                offsetY={24}
                                initials={`${thisConfig?.avatars?.metaData[1]?.fullName[0]}`}
                            />
                        </div>
                    }
                    {thisConfig?.avatars?.metaData[2]?.fullName &&
                        <div className={`${styles.avatar} ${styles["avatar-3"]} avatar`}>
                            <AvatarComponent
                                classes="rounded-full"
                                useGravatar={false}
                                size={44}
                                color="#FFFFFF"
                                background="#A13808"
                                fontSize={16}
                                fontWeight={400}
                                offsetY={24}
                                initials={`${thisConfig?.avatars?.metaData[2]?.fullName[0]}`}
                            />
                        </div>
                    }
                </div>
            </div>
            <div className={styles["container-content"]}>
                <div className={styles["title"]}>
                    <span>{operator}</span>:&nbsp;
                    <span>{stripSummary(props.chatMessageFragment)}</span>
                </div>
                <div className={styles["text-small"]}><span>chat</span> • <span><TimeSince time={time} setTimeSince={3000}></TimeSince></span></div>
            </div>
            <div className={styles["container-icon"]}>
                <svg xmlns="http://www.w3.org/2000/svg" width="71" height="71" viewBox="0 0 71 71" fill="black">
                    <path d="M15.9099 35.0018L43.1335 7.77818L35.3553 0L3.88908 31.4663L5.96048e-07 35.3553L35.3553 70.7107L43.4871 62.579L15.9099 35.0018Z" />
                </svg>
                {ureadMsgs > 0 && <div className={styles["unread-chats"]}>{ureadMsgs}</div>}
            </div>
        </div>
    );
};

export default Conversation;
